import { getField, updateField } from "vuex-map-fields";
import axios from "../plugins/axios";

const resolveErrorMessage = (error) => error?.response?.data?.message || "Something went wrong. Please try again.";

export default {
  namespaced: true,
  state: () => ({
    technologyList: [],
    technologyListLoading: false,
    verifiedStatusFilter: null,
  }),

  mutations: {
    updateField,
    setState(state, payload) {
      state[payload.key] = payload.value;
    },
    SAVE_TECHNOLOGY(state, payload) {
      let existingIndex = state.technologyList.findIndex((tech) => tech.entity_id == payload.entity_id);
      let list = state.technologyList;
      if (existingIndex >= 0) {
        list[existingIndex] = payload;
      } else {
        list.unshift(payload);
      }
      state.technologyList = list;
    },
  },

  getters: {
    getField,
    technologyList: (state) => state.technologyList,
    technologyListLoading: (state) => state.technologyListLoading,
    verifiedStatusFilter: (state) => state.verifiedStatusFilter,
  },

  actions: {
    async getTechnologyList({ commit }) {
      try {
        commit("setState", { key: "technologyListLoading", value: true });
        commit("startLoading", null, { root: true });
        const { data } = await axios.get("/technology/");
        if (!data.success) {
          return;
        }
        commit("setState", { key: "technologyList", value: data.results });
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "technologyListLoading", value: false });
        commit("stopLoading", null, { root: true });
      }
    },
    async searchTechnologyLogo({ commit }, { entity_id, domain, candidate_url, version }) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.post("/technology/logo/search", {
          entity_id,
          domain,
          candidate_url,
          version,
        });
        if (!data?.success || !data?.candidateUrl) {
          return {
            success: false,
            message: data?.message || "No candidate logo found for this domain.",
          };
        }
        if (data.data) {
          commit("SAVE_TECHNOLOGY", data.data);
        }
        return { success: true, candidateUrl: data.candidateUrl, technology: data.data };
      } catch (e) {
        console.error(e);
        return {
          success: false,
          conflict: e?.response?.status === 409,
          message: resolveErrorMessage(e),
        };
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async resolveTechnologyLogoCandidate({ commit, dispatch }, { entity_id, version, action }) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.post("/technology/logo/candidate", {
          entity_id,
          version,
          action,
        });
        if (!data?.success) {
          return {
            success: false,
            message: data?.message || "Could not update the candidate logo.",
          };
        }
        if (data.data) {
          commit("SAVE_TECHNOLOGY", data.data);
        }
        return { success: true, technology: data.data };
      } catch (e) {
        console.error(e);
        return {
          success: false,
          conflict: e?.response?.status === 409,
          message: resolveErrorMessage(e),
        };
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async saveTechnology({ commit, dispatch }, payload) {
      let imageFile = null;
      let techObject;
      if (payload.logo_image instanceof File) {
        imageFile = payload.logo_image;
        delete payload.logo_image;
      }
      try {
        commit("startLoading", null, { root: true });
        const { data: saveResponse } = await axios.post("/technology/", payload);
        if (!saveResponse.success) {
          dispatch("showMessage", { ...saveResponse, messageType: "error", messageTitle: "Error" }, { root: true });
          return { success: false };
        }
        techObject = saveResponse.data;
        if (imageFile != null) {
          const formData = new FormData();
          formData.append("entity_id", techObject.entity_id);
          formData.append("file", imageFile);
          // Same PUT /technology/logo route; multipart body → API puts to S3 (no browser CORS).
          const { data: logoResponse } = await axios.put("/technology/logo", formData, {
            transformRequest: [
              (body, headers) => {
                delete headers["Content-Type"];
                delete headers["content-type"];
                return body;
              },
            ],
          });
          if (!logoResponse.success) {
            dispatch("showMessage", { ...logoResponse, messageType: "error", messageTitle: "Error" }, { root: true });
            return { success: false };
          }
          if (logoResponse.data) {
            techObject = logoResponse.data;
          }
          techObject.imageUpdated = true;
        }
        commit("SAVE_TECHNOLOGY", techObject);
        return { success: true, technology: techObject };
      } catch (e) {
        console.error(e);
        dispatch(
          "showMessage",
          { message: resolveErrorMessage(e), messageType: "error", messageTitle: "Error" },
          { root: true }
        );
        return { success: false, conflict: e?.response?.status === 409 };
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
  },
};
